// Internal DriverWatch Admin Portal - Dashboard
// Shows all organizations with device count, user count and last activity

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;

export default function Dashboard() {
  const storedUser = JSON.parse(sessionStorage.getItem("internal_user") || "{}");
  const navigate = useNavigate();
  const token = sessionStorage.getItem("internal_token");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [formError, setFormError] = useState(null);
  const [orgFeatures, setOrgFeatures] = useState({}); // { orgId: { triggerwatch_enabled: "true" } }

  function fetchOrgs() {
    axios
      .get(`${API_URL}/internal/organizations`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      })
      .then((res) => {
        setOrgs(res.data);
        setLoading(false);
        res.data.forEach(org => fetchOrgFeatures(org.id));
      })
      .catch(() => {
        setError("Failed to load organizations");
        setLoading(false);
      });
  }

  function fetchOrgFeatures(orgId) {
    axios
      .get(`${API_URL}/internal/organizations/${orgId}/features`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      })
      .then((res) => {
        const featureMap = {};
        res.data.forEach(f => { featureMap[f.feature_key] = f.enabled; });
        setOrgFeatures(prev => ({ ...prev, [orgId]: featureMap }));
      })
      .catch(() => {});
  }

  async function toggleTriggerWatch(orgId) {
    const current = orgFeatures[orgId]?.triggerwatch_enabled;
    const newValue = current === "true" ? "false" : "true";
    try {
      await axios.put(
        `${API_URL}/internal/organizations/${orgId}/features/triggerwatch_enabled?value=${newValue}`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` } }
      );
      fetchOrgFeatures(orgId);
    } catch (err) {
      alert("Failed to update feature toggle");
    }
  }

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchOrgs();
  }, []);

  async function handleAddOrg(e) {
    e.preventDefault();
    setFormError(null);
    try {
      await axios.post(`${API_URL}/internal/organizations?name=${encodeURIComponent(newOrgName)}`, {}, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      setNewOrgName("");
      setShowAddOrg(false);
      fetchOrgs();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to create organization");
    }
  }

  async function handleDeleteOrg(orgId, orgName) {
    if (!window.confirm(`Delete ${orgName} and ALL associated data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/internal/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchOrgs();
    } catch (err) {
      setError("Failed to delete organization");
    }
  }

  async function handleUpgradeOrg(orgId, orgName) {
    if (!window.confirm(`Upgrade ${orgName} to a paid plan? This removes their device limit and trial expiry.`)) return;
    try {
      await axios.post(`${API_URL}/internal/organizations/${orgId}/upgrade`, {}, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchOrgs();
    } catch (err) {
      setError("Failed to upgrade organization");
    }
  }

  async function handleDowngradeToTrial(orgId, orgName) {
    if (!window.confirm(`Downgrade ${orgName} to trial? They'll get a fresh 30-day trial with a 30-device limit, replacing their paid plan.`)) return;
    try {
      await axios.post(`${API_URL}/internal/organizations/${orgId}/downgrade-to-trial`, {}, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchOrgs();
    } catch (err) {
      setError("Failed to downgrade organization");
    }
  }

  async function handleToggleSuspend(orgId, orgName, currentlySuspended) {
    const action = currentlySuspended ? "unsuspend" : "suspend";
    const confirmMsg = currentlySuspended
      ? `Unsuspend ${orgName}? This restores their full write access.`
      : `Suspend ${orgName}? They'll still be able to view their data, but all changes will be blocked until unsuspended.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.post(`${API_URL}/internal/organizations/${orgId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("internal_token")}` }
      });
      fetchOrgs();
    } catch (err) {
      setError(`Failed to ${action} organization`);
    }
  }

  // Summary totals
  const totalDevices = orgs.reduce((sum, org) => sum + org.device_count, 0);
  const totalUsers = orgs.reduce((sum, org) => sum + org.user_count, 0);

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* Navigation bar */}
      <nav className="bg-gray-900 border-b border-red-900 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-symbol.svg" alt="AleroSys" className="h-8 w-8" />
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">AleroSys</span>
            <span className="text-gray-500 text-lg font-light">—</span>
            <span className="text-red-400 text-lg font-semibold">DriverWatch Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">{storedUser.full_name} — {storedUser.role}</span>
          <button
            onClick={() => navigate("/users")}
            className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors"
          >
            Staff
          </button>
          <button
            onClick={() => navigate("/feature-requests")}
            className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors"
          >
            Feature Requests
          </button>
          <button
            onClick={() => navigate("/tools")}
            className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors"
          >
            Tools
          </button>
          <button
            onClick={() => {
              sessionStorage.removeItem("internal_token");
              sessionStorage.removeItem("internal_user");
              window.location.href = "/login";
            }}
            className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors"
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="px-8 py-8">

        {/* Page title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Organizations</h1>
            <p className="text-gray-400 mt-1">All MSP client organizations on the platform</p>
          </div>
          <button
            onClick={() => setShowAddOrg(!showAddOrg)}
            className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            {showAddOrg ? "Cancel" : "+ Add Organization"}
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Total Organizations</p>
            <p className="text-3xl font-bold text-white">{orgs.length}</p>
            <p className="text-gray-500 text-xs mt-2">Active clients</p>
          </div>
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Total Devices</p>
            <p className="text-3xl font-bold text-white">{totalDevices}</p>
            <p className="text-gray-500 text-xs mt-2">Across all orgs</p>
          </div>
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Total Users</p>
            <p className="text-3xl font-bold text-white">{totalUsers}</p>
            <p className="text-gray-500 text-xs mt-2">Across all orgs</p>
          </div>
        </div>

        {/* Add org form */}
        {showAddOrg && (
          <div className="bg-gray-900 rounded-xl p-6 border border-red-800 mb-8">
            <h2 className="text-white font-semibold mb-4">Add New Organization</h2>
            {formError && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={handleAddOrg} className="flex gap-4">
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Organization name"
                className="flex-1 bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-red-500"
                required
              />
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white text-sm px-6 py-2 rounded-lg transition-colors"
              >
                Create
              </button>
            </form>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Organizations table */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <h2 className="text-white font-semibold mb-4">All Organizations ({orgs.length})</h2>
          {orgs.length === 0 ? (
            <p className="text-gray-400">No organizations found</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left py-3">Organization</th>
                  <th className="text-left py-3">Enrollment Token</th>
                  <th className="text-left py-3">Devices</th>
                  <th className="text-left py-3">Users</th>
                  <th className="text-left py-3">Last Activity</th>
                  <th className="text-left py-3">Created</th>
                  <th className="text-left py-3">TriggerWatch</th>
                  <th className="text-left py-3">Plan</th>
                  <th className="text-left py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr key={org.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                    <td className="py-3 text-white font-medium">{org.name}</td>
                    <td className="py-3">
                      <code className="text-green-400 text-xs bg-gray-800 px-2 py-1 rounded">
                        {org.enrollment_token || "No token"}
                      </code>
                    </td>
                    <td className="py-3 text-gray-400">{org.device_count}</td>
                    <td className="py-3 text-gray-400">{org.user_count}</td>
                    <td className="py-3 text-gray-400">
                      {org.last_activity
                        ? new Date(org.last_activity).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => toggleTriggerWatch(org.id)}
                        className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          orgFeatures[org.id]?.triggerwatch_enabled === "true"
                            ? "bg-purple-900 text-purple-300 border border-purple-700"
                            : "bg-gray-800 text-gray-500 border border-gray-700"
                        }`}
                      >
                        {orgFeatures[org.id]?.triggerwatch_enabled === "true" ? "TriggerWatch: ON" : "TriggerWatch: OFF"}
                      </button>
                    </td>
                    <td className="py-3">
                      {org.status === "suspended" ? (
                        <span className="text-xs px-2 py-1 rounded bg-red-950 text-red-400 border border-red-800">
                          Suspended
                        </span>
                      ) : org.plan_type === "trial" ? (
                        <span className={`text-xs px-2 py-1 rounded ${org.status === "read_only" ? "bg-red-900 text-red-300" : "bg-blue-900 text-blue-300"}`}>
                          {org.status === "read_only" ? "Expired" : "Trial"}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-green-900 text-green-300">
                          Paid
                        </span>
                      )}
                    </td>
                    <td className="py-3 flex gap-2">
                      {org.plan_type === "trial" ? (
                        <button
                          onClick={() => handleUpgradeOrg(org.id, org.name)}
                          className="text-green-400 hover:text-green-300 text-xs border border-green-800 hover:border-green-600 px-2 py-1 rounded transition-colors"
                        >
                          Upgrade
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDowngradeToTrial(org.id, org.name)}
                          className="text-yellow-400 hover:text-yellow-300 text-xs border border-yellow-800 hover:border-yellow-600 px-2 py-1 rounded transition-colors"
                        >
                          Downgrade to Trial
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleSuspend(org.id, org.name, org.status === "suspended")}
                        className={`text-xs border px-2 py-1 rounded transition-colors ${
                          org.status === "suspended"
                            ? "text-blue-400 hover:text-blue-300 border-blue-800 hover:border-blue-600"
                            : "text-orange-400 hover:text-orange-300 border-orange-800 hover:border-orange-600"
                        }`}
                      >
                        {org.status === "suspended" ? "Unsuspend" : "Suspend"}
                      </button>
                      <button
                        onClick={() => handleDeleteOrg(org.id, org.name)}
                        className="text-red-400 hover:text-red-300 text-xs border border-red-800 hover:border-red-600 px-2 py-1 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </main>
    </div>
  );
}